import { ref, onMounted, onBeforeUnmount, watch, h } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import { Modal } from 'ant-design-vue'
import { ExclamationCircleOutlined } from '@ant-design/icons-vue'

/**
 * Composable for warning users about unsaved changes
 * @param {Object} options - Configuration options
 * @param {Function} options.hasChanges - Function that returns boolean indicating if there are unsaved changes
 * @param {String} options.message - Custom warning message (optional)
 * @returns {Object} - { hasUnsavedChanges, setHasUnsavedChanges, resetUnsavedChanges }
 */
export function useUnsavedChangesWarning(options = {}) {
  const {
    hasChanges,
    message = 'Anda memiliki perubahan yang belum disimpan. Yakin ingin meninggalkan halaman ini?',
  } = options

  const hasUnsavedChanges = ref(false)

  // Setup watcher if hasChanges function provided
  if (hasChanges && typeof hasChanges === 'function') {
    watch(
      hasChanges,
      newValue => {
        hasUnsavedChanges.value = newValue
      },
      { immediate: true },
    )
  }

  // Handle browser tab close or refresh
  const handleBeforeUnload = e => {
    if (hasUnsavedChanges.value) {
      e.preventDefault()
      e.returnValue = '' // Chrome requires returnValue to be set
      return ''
    }
  }

  // Handle navigation within the app
  onBeforeRouteLeave((to, from, next) => {
    if (hasUnsavedChanges.value) {
      Modal.confirm({
        title: 'Perubahan Belum Disimpan',
        icon: h(ExclamationCircleOutlined),
        content: message,
        okText: 'Ya, Tinggalkan',
        cancelText: 'Batal',
        okButtonProps: {
          danger: true,
        },
        onOk() {
          hasUnsavedChanges.value = false
          next()
        },
        onCancel() {
          next(false)
        },
      })
    } else {
      next()
    }
  })

  // Setup on mount
  onMounted(() => {
    window.addEventListener('beforeunload', handleBeforeUnload)
  })

  // Cleanup on unmount
  onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', handleBeforeUnload)
  })

  // Helper functions
  const setHasUnsavedChanges = value => {
    hasUnsavedChanges.value = value
  }

  const resetUnsavedChanges = () => {
    hasUnsavedChanges.value = false
  }

  // Helper to check navigation with confirmation
  const confirmNavigation = callback => {
    if (hasUnsavedChanges.value) {
      Modal.confirm({
        title: 'Perubahan Belum Disimpan',
        icon: h(ExclamationCircleOutlined),
        content: message,
        okText: 'Ya, Tinggalkan',
        cancelText: 'Batal',
        okButtonProps: {
          danger: true,
        },
        onOk() {
          hasUnsavedChanges.value = false
          callback()
        },
      })
    } else {
      callback()
    }
  }

  return {
    hasUnsavedChanges,
    setHasUnsavedChanges,
    resetUnsavedChanges,
    confirmNavigation,
  }
}
